import React, { HTMLProps, useEffect, useMemo, useRef, useState, useCallback } from 'react';

import { createPortal } from 'react-dom';
import { COLORS } from '../../../utils/colors';
import {
  ArrowsDownUp,
  SortDescending,
  SortAscending,
  Funnel,
  X,
  CaretLeft,
  CaretRight,
  Info,
} from 'phosphor-react';
import { twMerge } from 'tailwind-merge';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import emptyListImg from '../../../assets/images/emptyList.png';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DillSortDir = 'asc' | 'desc' | '';

export interface DillColumn<T = any> {
  key: string;
  label: React.ReactNode;
  width?: number;
  sticky?: boolean;
  stickyRight?: boolean;
  numeric?: boolean;
  sortable?: boolean;
  defaultSortDir?: 'asc' | 'desc';
  toggleable?: boolean;
  defaultHidden?: boolean;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  getSortValue?: (row: T) => string | number;
  getSearchValue?: (row: T) => string;
  footer?: React.ReactNode;
  headerAlign?: 'left' | 'right' | 'center';
  cellClassName?: string;
  filterable?: boolean;
  filterType?: 'values' | 'range' | 'dateRange';
  /** Row field to use for filter matching when different from `key` (e.g. filter by ID, display by name) */
  filterKey?: string;
  /** Map a raw filter value to a display label (used in filter panel checkboxes and active-filter chips) */
  getFilterLabel?: (value: string) => string;
}

export type ColRangeFilter = { type: 'range'; min: string; max: string };
export type ColValuesFilter = { type: 'values'; selected: string[] };
export type ColDateRangeFilter = { type: 'dateRange'; from: string; to: string };
export type ColFilterValue = ColRangeFilter | ColValuesFilter | ColDateRangeFilter;
export interface DillFilter {
  key: string;
  label: string;
  placeholder?: string;
  options: string[];
}

function isColFilterActive(f: ColFilterValue | undefined): boolean {
  if (!f) return false;
  if (f.type === 'range') return f.min !== '' || f.max !== '';
  if (f.type === 'dateRange') return f.from !== '' || f.to !== '';
  return f.selected.length > 0;
}

export interface DillSummaryCard {
  label: string;
  value: string;
  subLabel?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'danger' | 'warning' | 'success';
}

export interface DillRowAction<T = any> {
  key: string;
  icon: React.ReactNode;
  label?: string;
  variant?: 'default' | 'danger';
  onClick: (row: T, index: number, e: React.MouseEvent) => void;
}

export interface DillBulkAction<T = any> {
  key: string;
  label: string;
  variant?: 'default' | 'danger';
  onClick: (rows: T[], indices: number[]) => void;
}

export interface DillTableProps<T = any> {
  // ─ Core ──────────────────────────────────────────────────────────────────
  columns: DillColumn<T>[];
  data: T[];
  rowKey?: (row: T, index: number) => string | number;

  // ─ Sort ──────────────────────────────────────────────────────────────────
  sortable?: boolean;
  sortCycle?: '2state' | '3state';
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  onSortChange?: (key: string, dir: DillSortDir) => void;

  // ─ Search ────────────────────────────────────────────────────────────────
  search?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearch?: (val: string) => void;
  strictColumnSearch?: boolean;
  /**
   * The rows in `data` are already the search result, so skip local filtering.
   *
   * Without this the table re-filters server results by substring, which silently discards every
   * fuzzy or typo-tolerant match the server deliberately returned.
   */
  serverSideSearch?: boolean;

  // ─ Processed rows ─────────────────────────────────────────────────────────
  /** Emits the rows after search, filters, and sorting are applied (before pagination). Useful for exporting the filtered data. */
  onProcessedDataChange?: (rows: T[]) => void;

  // ─ Column toggle pills ───────────────────────────────────────────────────
  columnToggle?: boolean;

  // ─ Summary cards (optional) ─────────────────────────────────────────────
  summaryCards?: DillSummaryCard[];

  // ─ Empty / loading ───────────────────────────────────────────────────────
  loading?: boolean;
  loadingMore?: boolean;
  loadingText?: string;
  emptyMessage?: string;
  emptyAction?: { label: string; icon?: React.ReactNode; onClick: () => void };

  // ─ Striped rows ──────────────────────────────────────────────────────────
  striped?: boolean;
  hoverVariant?: 'default' | 'info';

  // ─ Selectable rows (OPTIONAL now) ─────────────────────────────────────
  selectable?: boolean;
  onSelectionChange?: (rows: T[], indices: number[]) => void;
  /** Controlled selection - set of row IDs (uses rowKey or 'id' to match) */
  selectedRowIds?: Set<number | string>;

  // ─ Bulk actions ──────────────────────────────────────────────────────────
  bulkActions?: DillBulkAction<T>[];

  // ─ Row actions ─────────────────────────────────────────────────────────────
  rowActions?: DillRowAction<T>[];
  rowActionsMode?: 'hover' | 'always';
  /** Pin the actions column to the right edge so it survives horizontal scrolling. */
  stickyRowActions?: boolean;

  // ─ Row behaviour ─────────────────────────────────────────────────────────
  onRowClick?: (row: T, index: number, event: React.MouseEvent) => void;
  rowClassName?: (row: T, index: number) => string;
  isRowSelectable?: (row: T, index: number) => boolean;
  getRowDisabledReason?: (row: T, index: number) => string | undefined;

  // ─ Labels ────────────────────────────────────────────────────────────────
  countLabel?: string;

  // ─ Container ─────────────────────────────────────────────────────────────
  className?: string;
  maxHeight?: string;
  scrollX?: boolean;

  // ─ Virtualization ─────────────────────────────────────────────────────────
  virtualize?: boolean;
  rowHeight?: number;
  rowPadding?: number;

  // ─ Toolbar filters ──────────────────────────────────────────────────────
  /** Dropdown filter definitions shown in the toolbar */
  filters?: DillFilter[];
  /** Controlled filter values */
  filterValues?: Record<string, string>;
  /** Controlled filter handler */
  onFilterChange?: (key: string, val: string) => void;
  /** Initial column-level filter values (keyed by column key) */
  defaultColFilters?: Record<string, ColFilterValue>;
  addButton?: { label: string; icon?: React.ReactNode; onClick: () => void };
  toolbarLeft?: React.ReactNode;
  toolbarNextToSearch?: React.ReactNode;
  /** Rendered as its own row between the toolbar (search/filters/buttons) and the table body -
   * same slot the native active-filter-chips row and the bulk-action bar occupy, in that order. */
  belowToolbar?: React.ReactNode;

  // ─ Footer ────────────────────────────────────────────────────────────────
  showFooter?: boolean;
  /** Pin the totals row to the bottom of the scroll container (default: true) */
  stickyFooter?: boolean;

  // ─ Pagination ────────────────────────────────────────────────────────────
  pagination?: boolean;
  pageSize?: number;
  totalCount?: number;

  // ─ View ──────────────────────────────────────────────────────────────────
  cardView?: boolean;
  toolbarRight?: React.ReactNode;
  countAddon?: React.ReactNode;

  // ─ Column resizing ────────────────────────────────────────────────────────
  resizable?: boolean;
  showCount?: boolean;
  /** Show active-filter chips bar when any filter is applied (default: true) */
  showActiveFilters?: boolean;
  searchClassName?: string;
  /** Width (px) below which the table switches to a card-per-row layout */
  mobileBreakpoint?: number;
  /** Custom card renderer for mobile view; falls back to the built-in layout when omitted */
  renderMobileCard?: (row: T, index: number) => React.ReactNode;
  tableClassName?: string;
  /** Show vertical dividers (border-right) between columns */
  verticalDividers?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const cardVariantCls: Record<NonNullable<DillSummaryCard['variant']>, string> = {
  default: 'bg-blue-50 text-blue-700',
  danger: 'bg-red-50 text-red-700',
  warning: 'bg-amber-50 text-amber-700',
  success: 'bg-green-50 text-green-700',
};

const actionVariantCls: Record<'default' | 'danger', string> = {
  default: 'border-gray-200 bg-white hover:bg-gray-100 text-gray-500',
  danger: 'border-red-100 bg-red-50 hover:bg-red-100 text-red-500',
};

function SortIcon({ active, dir }: { active: boolean; dir: DillSortDir }) {
  if (!active) return <ArrowsDownUp size={12} className="text-gray-300 shrink-0" />;
  return dir === 'asc' ? (
    <SortDescending size={12} className="text-blue-500 shrink-0" />
  ) : (
    <SortAscending size={12} className="text-blue-500 shrink-0" />
  );
}

// Pagination component
function Pagination({
  page,
  pageCount,
  from,
  to,
  total,
  label,
  onPage,
}: {
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
  label: string;
  onPage: (p: number) => void;
}) {
  const pages = useMemo(() => {
    const range: (number | '…')[] = [];
    for (let i = 0; i < pageCount; i++) {
      if (i === 0 || i === pageCount - 1 || Math.abs(i - page) <= 1) {
        range.push(i);
      } else if (range[range.length - 1] !== '…') {
        range.push('…');
      }
    }
    return range;
  }, [page, pageCount]);

  if (pageCount <= 1) return null;

  const btnCls =
    'w-7 h-7 flex items-center justify-center rounded-md border text-xs transition-colors';

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50 shrink-0 select-none">
      <span className="text-xs text-gray-400">
        Showing {from}–{to} of {total} {label}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 0}
          className={twMerge(
            btnCls,
            'border-transparent text-gray-400 hover:border-gray-200 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed'
          )}>
          <CaretLeft size={13} />
        </button>
        {pages.map((p, i) =>
          p === '…' ? (
            <span
              key={`e${i}`}
              className="w-7 flex items-center justify-center text-xs text-gray-400">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPage(p as number)}
              className={twMerge(
                btnCls,
                p === page
                  ? 'border-blue-200 bg-blue-50 text-blue-500 font-medium'
                  : 'border-transparent text-gray-500 hover:border-gray-200 hover:bg-white'
              )}>
              {(p as number) + 1}
            </button>
          )
        )}
        <button
          onClick={() => onPage(page + 1)}
          disabled={page === pageCount - 1}
          className={twMerge(
            btnCls,
            'border-transparent text-gray-400 hover:border-gray-200 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed'
          )}>
          <CaretRight size={13} />
        </button>
      </div>
    </div>
  );
}

const CHECKMARK_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.207 4.793a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L6.5 9.086l4.293-4.293a1 1 0 0 1 1.414 0z'/%3E%3C/svg%3E")`;
const INDETERMINATE_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='3' y='7' width='10' height='2' rx='1'/%3E%3C/svg%3E")`;

// Indeterminate checkbox component
const IndeterminateCheckbox = ({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current && typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  const checkedColor = COLORS.PRIMARY_500;
  const isIndeterminate = !rest.checked && indeterminate;
  const isActive = rest.checked || isIndeterminate;

  return (
    <input
      type="checkbox"
      ref={ref}
      {...rest}
      className={twMerge(
        'w-4 h-4 cursor-pointer shrink-0 appearance-none border border-gray-300 rounded-[4px]',
        className
      )}
      style={{
        backgroundColor: isActive ? checkedColor : 'white',
        borderColor: isActive ? checkedColor : undefined,
        backgroundImage: rest.checked
          ? CHECKMARK_SVG
          : isIndeterminate
          ? INDETERMINATE_SVG
          : 'none',
      }}
    />
  );
};

// ─── Virtual Body (isolated so scroll doesn't re-render the full table) ──────

// How many rows to paint when the virtualizer has not measured the viewport yet. Enough to fill
// any realistic screen; scrolling re-measures and hands control back to the virtualizer.
const UNMEASURED_ROW_FALLBACK = 40;

/**
 * Width of the row-actions column: one 28px button plus its gap, per action.
 *
 * Fixed at 80px it fitted two icons only by squashing them, and the sticky variant needs a real
 * number anyway — a sticky-right actions column has to reserve exactly the space that
 * stickyRight data columns are offset by, or the two overlap.
 */
const ROW_ACTION_SLOT = 32;
const ROW_ACTIONS_MIN_WIDTH = 80;
const getRowActionsWidth = (count: number): number =>
  count > 0 ? Math.max(ROW_ACTIONS_MIN_WIDTH, 24 + count * ROW_ACTION_SLOT) : 0;

type StickyOffsets = Record<
  string,
  { left?: number; right?: number; edgeLeft?: boolean; edgeRight?: boolean; firstLeft?: boolean }
>;

interface VBodyProps<T = any> {
  rows: T[];
  rowHeight: number;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  loading: boolean;
  loadingText: string;
  colCount: number;
  emptyMessage: string;
  emptyAction?: { label: string; icon?: React.ReactNode; onClick: () => void };
  selectable: boolean;
  selectedIndices: Set<number>;
  toggleSelectRow: (i: number) => void;
  visibleColumns: DillColumn<T>[];
  rowActions?: DillRowAction<T>[];
  rowActionsMode: 'hover' | 'always';
  stickyRowActions: boolean;
  onRowClick?: (row: T, index: number, event: React.MouseEvent) => void;
  rowClassName?: (row: T, index: number) => string;
  isRowSelectable?: (row: T, index: number) => boolean;
  getRowDisabledReason?: (row: T, index: number) => string | undefined;
  striped: boolean;
  hoverVariant: 'default' | 'info';
  rowKey?: (row: T, index: number) => string | number;
  stickyOffsets: StickyOffsets;
  rowPadding: number;
  resizable: boolean;
  verticalDividers: boolean;
}

const DillVirtualBody = React.memo(function DillVirtualBody({
  rows,
  rowHeight,
  scrollContainerRef,
  loading,
  loadingText,
  colCount,
  emptyMessage,
  emptyAction,
  selectable,
  selectedIndices,
  toggleSelectRow,
  visibleColumns,
  rowActions,
  rowActionsMode,
  stickyRowActions,
  onRowClick,
  rowClassName,
  isRowSelectable,
  getRowDisabledReason,
  striped,
  hoverVariant,
  rowKey,
  stickyOffsets,
  rowPadding,
  resizable,
  verticalDividers,
}: VBodyProps) {
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight,
    getScrollElement: () => scrollContainerRef.current,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  /**
   * Re-render once after mount so the virtualizer sees a measured container.
   *
   * On the first render the scroll container ref is still null, so the virtualizer falls back to
   * a zero-height rect — and virtual-core returns a null range for a zero-height viewport, i.e.
   * no rows at all. It attaches to the container in a layout effect, but this body is memoised,
   * so without a nudge nothing re-reads the measurement and the table stays blank until some
   * unrelated prop happens to change.
   */
  const [, remeasure] = useState(0);
  useEffect(() => {
    remeasure(1);
  }, []);

  const measuredItems = rowVirtualizer.getVirtualItems();
  const totalVirtualSize = rowVirtualizer.getTotalSize();

  /**
   * Last resort: show rows the virtualizer declined to place.
   *
   * Anything that leaves the container unmeasured — mounting inside a collapsed flex parent, a
   * tab that becomes visible without resizing, a ResizeObserver notification that lands after
   * the last render — reads to the user as "the data vanished", with the row count still saying
   * otherwise. Rows are a fixed rowHeight, so their offsets are computable without measuring.
   * Capped because an unmeasured viewport gives no hint of how many rows would actually fit.
   */
  const virtualItems = useMemo(() => {
    if (measuredItems.length > 0 || rows.length === 0) {
      return measuredItems;
    }
    return Array.from({ length: Math.min(rows.length, UNMEASURED_ROW_FALLBACK) }, (_, index) => ({
      index,
      key: index,
      start: index * rowHeight,
      end: (index + 1) * rowHeight,
      size: rowHeight,
      lane: 0,
    }));
  }, [measuredItems, rows.length, rowHeight]);

  return (
    <tbody
      style={{
        display: 'grid',
        height: loading || rows.length === 0 ? undefined : totalVirtualSize,
        position: 'relative',
      }}>
      {loading && (
        <tr>
          <td colSpan={colCount} className="py-10 text-center text-xs text-gray-400">
            <div className="flex items-center justify-center gap-2 w-[calc(100vw-200px)] h-full">
              <svg
                className="animate-spin"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              {loadingText}
            </div>
          </td>
        </tr>
      )}
      {!loading && rows.length === 0 && (
        <tr>
          <td colSpan={colCount} className="py-16">
            <div className="flex flex-col items-center justify-center gap-3 w-[calc(100vw-200px)] h-full">
              <img
                src={emptyListImg}
                alt=""
                className="w-40 object-contain opacity-40 mix-blend-mode-multiply"
                style={{ mixBlendMode: 'multiply' }}
              />
              <p className="text-xs text-gray-400">{emptyMessage}</p>
              {emptyAction && (
                <button
                  onClick={emptyAction.onClick}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-blue-500 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                  {emptyAction.icon}
                  {emptyAction.label}
                </button>
              )}
            </div>
          </td>
        </tr>
      )}
      {!loading &&
        virtualItems.map((virtualRow) => {
          const row = rows[virtualRow.index];
          const globalIndex = virtualRow.index;
          const key = rowKey ? rowKey(row, globalIndex) : globalIndex;
          const extraCls = rowClassName ? rowClassName(row, globalIndex) : '';
          const clickable = !!onRowClick;
          const isSelected = selectable && selectedIndices.has(globalIndex);
          const rowSelectable = !isRowSelectable || isRowSelectable(row, globalIndex);

          return (
            <tr
              key={key}
              data-index={virtualRow.index}
              ref={(node) => rowVirtualizer.measureElement(node)}
              onClick={clickable ? (e) => onRowClick?.(row, globalIndex, e) : undefined}
              className={twMerge(
                'border-b border-gray-100 transition-colors group',
                clickable && 'cursor-pointer',
                hoverVariant === 'info' ? 'hover:bg-blue-50' : 'hover:bg-gray-50',
                striped && virtualRow.index % 2 === 1 && 'bg-gray-50',
                isSelected && 'bg-blue-50',
                extraCls
              )}
              style={{
                display: 'flex',
                position: 'absolute',
                top: virtualRow.start,
                width: '100%',
                height: rowHeight,
              }}>
              {selectable && (
                <td
                  className={twMerge(
                    'px-3 w-10 flex items-center flex-shrink-0 sticky left-0 border-b border-gray-100 z-10 transition-colors',
                    !isSelected &&
                      (hoverVariant === 'info'
                        ? 'bg-white group-hover:bg-blue-50'
                        : 'bg-white group-hover:bg-gray-50'),
                    isSelected && 'bg-blue-50'
                  )}
                  style={{
                    position: 'sticky',
                    paddingTop: rowPadding,
                    paddingBottom: rowPadding,
                  }}
                  onClick={(e) => e.stopPropagation()}>
                  {rowSelectable ? (
                    <IndeterminateCheckbox
                      checked={isSelected}
                      onChange={() => toggleSelectRow(globalIndex)}
                    />
                  ) : (
                    getRowDisabledReason &&
                    (() => {
                      const reason = getRowDisabledReason(row, globalIndex);
                      return reason ? (
                        <span
                          data-tooltip-id="dill-table-disabled-row"
                          data-tooltip-content={reason}
                          className="flex items-center text-gray-400 cursor-help text-xs">
                          <Info size={14} />
                        </span>
                      ) : null;
                    })()
                  )}
                </td>
              )}
              {visibleColumns.map((col) => {
                const rawVal = (row as any)[col.key];
                const cell = col.render ? col.render(rawVal, row, globalIndex) : rawVal;
                const sticky = stickyOffsets[col.key];
                return (
                  <td
                    key={col.key}
                    className={twMerge(
                      'px-4 text-gray-700 flex-shrink-0 flex items-center border-b border-gray-100',
                      sticky?.firstLeft && selectable && 'pl-1',
                      col.numeric && 'justify-end tabular-nums',
                      col.cellClassName,
                      verticalDividers && 'border-r border-gray-200 last:border-r-0',
                      sticky && 'transition-colors',
                      sticky &&
                        !isSelected &&
                        (hoverVariant === 'info'
                          ? 'bg-white group-hover:bg-blue-50'
                          : 'bg-white group-hover:bg-gray-50'),
                      sticky && isSelected && 'bg-blue-50',
                      sticky?.edgeLeft && 'border-r border-gray-100',
                      sticky?.edgeRight && 'border-l border-gray-100',
                      sticky && 'z-[1] group-hover:z-[2]'
                    )}
                    style={{
                      paddingTop: rowPadding,
                      paddingBottom: rowPadding,
                      width: col.width,
                      minWidth: resizable ? 60 : col.width,
                      ...(sticky
                        ? {
                            position: 'sticky',
                            left: sticky.left,
                            right: sticky.right,
                          }
                        : {}),
                    }}>
                    {cell ?? '—'}
                  </td>
                );
              })}
              {rowActions && rowActions.length > 0 && (
                <td
                  className={twMerge(
                    'px-3 flex-shrink-0 flex items-center justify-end',
                    // A sticky cell cannot be transparent or the row scrolls through it, so it
                    // has to reproduce the row's own background — including the hover and
                    // selected states, which are set on the row.
                    stickyRowActions && 'border-l border-gray-100',
                    stickyRowActions &&
                      !isSelected &&
                      (hoverVariant === 'info'
                        ? 'bg-white group-hover:bg-blue-50'
                        : 'bg-white group-hover:bg-gray-50'),
                    stickyRowActions && striped && virtualRow.index % 2 === 1 && 'bg-gray-50',
                    stickyRowActions && isSelected && 'bg-blue-50'
                  )}
                  style={{
                    paddingTop: rowPadding,
                    paddingBottom: rowPadding,
                    width: getRowActionsWidth(rowActions.length),
                    ...(stickyRowActions ? { position: 'sticky', right: 0, zIndex: 10 } : {}),
                  }}
                  onClick={(e) => e.stopPropagation()}>
                  <div
                    className={twMerge(
                      'flex items-center justify-end gap-1 transition-opacity',
                      rowActionsMode === 'always'
                        ? 'opacity-100'
                        : 'opacity-0 group-hover:opacity-100'
                    )}>
                    {rowActions.map((action) => (
                      <button
                        key={action.key}
                        title={action.label}
                        onClick={(e) => action.onClick(row, globalIndex, e)}
                        className={twMerge(
                          'w-7 h-7 shrink-0 flex items-center justify-center rounded-md border transition-colors',
                          actionVariantCls[action.variant ?? 'default']
                        )}>
                        {action.icon}
                      </button>
                    ))}
                  </div>
                </td>
              )}
            </tr>
          );
        })}
    </tbody>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────

function DillTableImpl<T = any>(
  {
    columns,
    data,
    rowKey,

    sortable = true,
    sortCycle = '3state',
    defaultSort,
    onSortChange,

    search = true,
    searchPlaceholder = 'Search…',
    searchValue: searchValueProp,
    onSearch,
    strictColumnSearch = false,
    serverSideSearch = false,
    onProcessedDataChange,

    columnToggle,

    summaryCards,

    loading = false,
    loadingMore = false,
    loadingText = 'Loading…',
    emptyMessage = 'No data found',
    emptyAction,

    striped = true,
    hoverVariant = 'default',

    selectable = false,
    onSelectionChange,
    selectedRowIds,

    bulkActions,

    rowActions,
    rowActionsMode = 'hover',
    stickyRowActions = false,

    onRowClick,
    rowClassName,
    isRowSelectable,
    getRowDisabledReason,

    countLabel,

    className,
    maxHeight,
    scrollX = false,

    rowHeight = 48,
    rowPadding = 12,
    totalCount,
    toolbarRight,
    countAddon,
    showFooter = false,
    stickyFooter = true,
    filters,
    filterValues: filterValuesProp,
    onFilterChange,
    defaultColFilters,
    toolbarLeft,
    belowToolbar,
    addButton,
    pagination = false,
    pageSize = 20,
    resizable = false,
    showCount = true,
    showActiveFilters = false,
    searchClassName,
    mobileBreakpoint,
    renderMobileCard,
    tableClassName,
    verticalDividers = false,
  }: DillTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  // ─ Internal sort state ────────────────────────────────────────────────────
  const [intSortKey, setIntSortKey] = useState<string>(defaultSort?.key ?? '');
  const [intSortDir, setIntSortDir] = useState<DillSortDir>(defaultSort?.dir ?? '');

  const sortKey = onSortChange ? '' : intSortKey;
  const sortDir = onSortChange ? '' : intSortDir;

  const handleSort = useCallback(
    (key: string) => {
      if (loading) return;
      if (onSortChange) {
        onSortChange(key, 'asc');
        return;
      }
      const colDef = columns.find((c) => c.key === key);
      const firstDir: DillSortDir = colDef?.defaultSortDir ?? 'asc';
      const secondDir: DillSortDir = firstDir === 'asc' ? 'desc' : 'asc';
      setIntSortKey((prev) => {
        if (prev !== key) {
          setIntSortDir(firstDir);
          return key;
        }
        setIntSortDir((d) => {
          if (d === '') return firstDir;
          if (d === firstDir) return secondDir;
          if (sortCycle === '2state') return firstDir;
          setIntSortKey('');
          return '';
        });
        return prev;
      });
    },
    [loading, onSortChange, sortCycle, columns]
  );

  // ─ Internal search state ──────────────────────────────────────────────────
  const [intSearch, setIntSearch] = useState('');
  const searchVal = onSearch ? searchValueProp ?? '' : intSearch;

  const handleSearch = useCallback(
    (v: string) => {
      if (onSearch) onSearch(v);
      else setIntSearch(v);
    },
    [onSearch]
  );

  // ─ Toolbar dropdown filter state ─────────────────────────────────────────
  const [intFilters, setIntFilters] = useState<Record<string, string>>({});
  const filterVals = useMemo(
    () => (onFilterChange ? filterValuesProp ?? {} : intFilters),
    [onFilterChange, filterValuesProp, intFilters]
  );

  const handleFilter = useCallback(
    (key: string, val: string) => {
      if (onFilterChange) onFilterChange(key, val);
      else setIntFilters((p) => ({ ...p, [key]: val }));
    },
    [onFilterChange]
  );

  // ─ Column-level filters ───────────────────────────────────────────────────
  const [colFilters, setColFilters] = useState<Record<string, ColFilterValue>>(
    () => defaultColFilters ?? {}
  );
  const defaultColFiltersApplied = useRef(!!defaultColFilters);
  useEffect(() => {
    if (
      !defaultColFiltersApplied.current &&
      defaultColFilters &&
      Object.keys(defaultColFilters).length > 0
    ) {
      setColFilters(defaultColFilters);
      defaultColFiltersApplied.current = true;
    }
  }, [defaultColFilters]);

  const [activeFilterCol, setActiveFilterCol] = useState<string | null>(null);
  const [filterAnchor, setFilterAnchor] = useState({ top: 0, left: 0 });
  const [valFilterSearch, setValFilterSearch] = useState<Record<string, string>>({});
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const handleFilterIconClick = useCallback(
    (colKey: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (activeFilterCol === colKey) {
        setActiveFilterCol(null);
        return;
      }
      const th = (e.currentTarget as HTMLElement).closest('th');
      if (th) {
        const rect = th.getBoundingClientRect();
        const PANEL_W = 230;
        const left = rect.left + PANEL_W > window.innerWidth ? rect.right - PANEL_W : rect.left;
        setFilterAnchor({ top: rect.bottom + 4, left });
      }
      setActiveFilterCol(colKey);
    },
    [activeFilterCol]
  );

  const handleRangeChange = useCallback((colKey: string, field: 'min' | 'max', val: string) => {
    setColFilters((prev) => ({
      ...prev,
      [colKey]: {
        type: 'range',
        min: (prev[colKey] as ColRangeFilter | undefined)?.min ?? '',
        max: (prev[colKey] as ColRangeFilter | undefined)?.max ?? '',
        [field]: val,
      } as ColRangeFilter,
    }));
  }, []);

  const handleDateRangeChange = useCallback((colKey: string, field: 'from' | 'to', val: string) => {
    setColFilters((prev) => ({
      ...prev,
      [colKey]: {
        type: 'dateRange',
        from: (prev[colKey] as ColDateRangeFilter | undefined)?.from ?? '',
        to: (prev[colKey] as ColDateRangeFilter | undefined)?.to ?? '',
        [field]: val,
      } as ColDateRangeFilter,
    }));
  }, []);

  const handleToggleValue = useCallback((colKey: string, val: string) => {
    setColFilters((prev) => {
      const existing = prev[colKey] as ColValuesFilter | undefined;
      const selected = existing?.selected ?? [];
      return {
        ...prev,
        [colKey]: {
          type: 'values',
          selected: selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val],
        } as ColValuesFilter,
      };
    });
  }, []);

  const clearColFilter = useCallback((colKey: string) => {
    setColFilters((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
    setActiveFilterCol(null);
  }, []);

  // ─ Active filters (derived from column + toolbar filters) ─────────────────
  const activeFilters = useMemo(() => {
    const result: { id: string; label: string; value: string; source: 'column' | 'toolbar' }[] = [];

    // Column-level filters
    Object.entries(colFilters).forEach(([key, filter]) => {
      if (!isColFilterActive(filter)) return;
      const col = columns.find((c) => c.key === key);
      const label = col ? (typeof col.label === 'string' ? col.label : key) : key;
      let value = '';
      if (filter.type === 'range') {
        const parts: string[] = [];
        if (filter.min !== '') parts.push(`Min: ${filter.min}`);
        if (filter.max !== '') parts.push(`Max: ${filter.max}`);
        value = parts.join(', ');
      } else if (filter.type === 'dateRange') {
        const parts: string[] = [];
        if (filter.from !== '') parts.push(`From: ${filter.from}`);
        if (filter.to !== '') parts.push(`To: ${filter.to}`);
        value = parts.join(', ');
      } else if (filter.type === 'values') {
        const mapLabel = col?.getFilterLabel;
        value = mapLabel ? filter.selected.map(mapLabel).join(', ') : filter.selected.join(', ');
      }
      result.push({ id: key, label, value, source: 'column' });
    });

    // Toolbar dropdown filters
    if (filters) {
      filters.forEach((f) => {
        const val = filterVals[f.key] ?? '';
        if (val) result.push({ id: f.key, label: f.label, value: val, source: 'toolbar' });
      });
    }

    return result;
  }, [colFilters, columns, filters, filterVals]);

  const handleClearAllFilters = useCallback(() => {
    setColFilters({});
    setActiveFilterCol(null);
    if (filters) {
      filters.forEach((f) => {
        if (filterVals[f.key]) handleFilter(f.key, '');
      });
    }
  }, [filters, filterVals, handleFilter]);

  const handleClearSingleFilter = useCallback(
    (id: string, source: 'column' | 'toolbar') => {
      if (source === 'column') {
        clearColFilter(id);
      } else {
        handleFilter(id, '');
      }
    },
    [clearColFilter, handleFilter]
  );

  useEffect(() => {
    if (!activeFilterCol) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target as Node)) {
        setActiveFilterCol(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeFilterCol]);

  // ─ Column visibility ──────────────────────────────────────────────────────
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(columns.filter((c) => c.toggleable && c.defaultHidden).map((c) => c.key))
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => !hiddenCols.has(c.key)),
    [columns, hiddenCols]
  );

  // ─ Column resizing ────────────────────────────────────────────────────────
  // colWidths stores raw weights (initialized from col.width). When resizable,
  // these are normalized to fill the container width proportionally.
  const [colWidths, setColWidths] = useState<Record<string, number>>(() =>
    Object.fromEntries(columns.map((c) => [c.key, c.width ?? 120]))
  );

  // ─ Mobile card view ──────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !mobileBreakpoint) return false;
    return window.innerWidth < mobileBreakpoint;
  });

  useEffect(() => {
    if (!mobileBreakpoint) return;
    const handleResize = () => setIsMobile(window.innerWidth < mobileBreakpoint);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  // Measure the table wrapper width so we can fill it proportionally.
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!resizable && !scrollX) return;
    const el = tableWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      let padding = 8;
      if (selectable) {
        padding = 42;
      }
      setContainerWidth(entry.contentRect.width - padding);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [resizable, scrollX]);

  // Calculate total width of visible columns (before resizing)
  const totalColumnWidth = useMemo(() => {
    return visibleColumns.reduce((sum, col) => sum + (col.width ?? 120), 0);
  }, [visibleColumns]);

  // Auto-fit when scrollX is enabled and container is wider than column total
  const shouldAutoFit = scrollX && !resizable && containerWidth > totalColumnWidth;
  const effectiveResizable = resizable || shouldAutoFit;

  // Merge dynamic widths into visible columns so body + header stay in sync.
  // When resizable or auto-fit, normalize weights to fill containerWidth proportionally.
  const effectiveColumns = useMemo(() => {
    const cols = visibleColumns.map((col) => ({
      ...col,
      width: effectiveResizable ? colWidths[col.key] ?? col.width : col.width,
    }));

    if (!effectiveResizable || containerWidth === 0) return cols;

    const totalW = cols.reduce((s, c) => s + (c.width ?? 120), 0);
    if (totalW === 0) return cols;

    let resizeWidth = false;
    if (containerWidth > totalW) {
      resizeWidth = true;
    }

    return cols.map((col) => ({
      ...col,
      ...((resizeWidth || resizable) && {
        width: Math.max(60, Math.round(((col.width ?? 120) / totalW) * containerWidth)),
      }),
    }));
  }, [visibleColumns, colWidths, effectiveResizable, containerWidth]);

  const onResizeMouseDown = useCallback(
    (key: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = colWidths[key] ?? 120;
      const onMove = (ev: MouseEvent) => {
        setColWidths((prev) => ({ ...prev, [key]: Math.max(60, startW + ev.clientX - startX) }));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [colWidths]
  );

  // ─ Sticky column offsets ──────────────────────────────────────────────────
  const stickyOffsets = useMemo<StickyOffsets>(() => {
    const result: StickyOffsets = {};
    let left = selectable ? 40 : 0;
    let lastLeftKey = '';
    let isFirstLeftSticky = true;
    for (const col of effectiveColumns) {
      if (col.sticky) {
        result[col.key] = {
          left,
          ...(selectable && isFirstLeftSticky ? { firstLeft: true } : {}),
        };
        left += col.width ?? 0;
        lastLeftKey = col.key;
        isFirstLeftSticky = false;
      }
    }
    if (lastLeftKey) result[lastLeftKey].edgeLeft = true;

    let right = getRowActionsWidth(rowActions?.length ?? 0);
    let lastRightKey = '';
    for (const col of [...effectiveColumns].reverse()) {
      if (col.stickyRight) {
        result[col.key] = { right };
        right += col.width ?? 0;
        lastRightKey = col.key;
      }
    }
    if (lastRightKey) result[lastRightKey].edgeRight = true;
    return result;
  }, [effectiveColumns, selectable, rowActions]);

  const toggleCol = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const nonStickyVisible = visibleColumns.filter((c) => !c.sticky).length;
        if (nonStickyVisible <= 1) return prev;
        next.add(key);
      }
      return next;
    });
  };

  // ─ FIXED: Unique values for text column filters (FROM PROCESSED DATA) ─────
  // This is the KEY FIX - we extract unique values AFTER initial filtering
  const colUniqueValues = useMemo(() => {
    const map: Record<string, string[]> = {};
    const processedForFilters = [...data];

    // Apply search + sort to get a more representative dataset
    let filtered = [...processedForFilters];
    if (searchVal.trim() && !serverSideSearch) {
      const q = searchVal.toLowerCase();
      filtered = filtered.filter((r) => {
        const flat = Object.values(r as any)
          .map((v) => {
            if (Array.isArray(v))
              return v
                .map((item) =>
                  typeof item === 'object' && item !== null
                    ? Object.values(item).join(' ')
                    : String(item ?? '')
                )
                .join(' ');
            if (typeof v === 'object' && v !== null) return Object.values(v).join(' ');
            return String(v ?? '');
          })
          .join(' ')
          .toLowerCase();
        return flat.includes(q);
      });
    }

    visibleColumns
      .filter((c) => c.filterable && !c.numeric && c.filterType !== 'dateRange')
      .forEach((col) => {
        const fk = col.filterKey ?? col.key;
        const set = new Set<string>();
        filtered.forEach((row) => {
          const v = (row as any)[fk];
          if (v !== null && v !== undefined && v !== '') set.add(String(v));
        });
        map[col.key] = Array.from(set).sort((a, b) => {
          if (col.getFilterLabel) {
            return col.getFilterLabel(a).localeCompare(col.getFilterLabel(b));
          }
          return a.localeCompare(b);
        });
      });

    // For numeric columns, extract min/max ranges
    visibleColumns
      .filter((c) => c.filterable && c.numeric)
      .forEach((col) => {
        const fk = col.filterKey ?? col.key;
        const values: number[] = [];
        filtered.forEach((row) => {
          const v = (row as any)[fk];
          if (typeof v === 'number' && !isNaN(v)) values.push(v);
        });
        if (values.length > 0) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          map[col.key] = [String(min), String(max)]; // Store for reference
        }
      });

    return map;
  }, [visibleColumns, data, searchVal, serverSideSearch]);

  // ─ FIXED: Filtered + sorted data (COMPLETE REFACTOR) ─────────────────────
  const processed = useMemo(() => {
    let rows = [...data];

    // 1. Apply search FIRST
    if (searchVal.trim() && !serverSideSearch) {
      const q = searchVal.toLowerCase();
      const searchCols = columns.filter((c) => c.getSearchValue);
      if (strictColumnSearch) {
        rows = rows.filter((r) =>
          columns.some((c) => {
            const val = c.getSearchValue
              ? c.getSearchValue(r)
              : String((r as any)[c.filterKey ?? c.key] ?? '');
            return val.toLowerCase().includes(q);
          })
        );
      } else {
        rows = rows.filter((r) => {
          const flat = Object.values(r as any)
            .map((v) => {
              if (Array.isArray(v))
                return v
                  .map((item) =>
                    typeof item === 'object' && item !== null
                      ? Object.values(item).join(' ')
                      : String(item ?? '')
                  )
                  .join(' ');
              if (typeof v === 'object' && v !== null) return Object.values(v).join(' ');
              return String(v ?? '');
            })
            .join(' ')
            .toLowerCase();
          if (flat.includes(q)) return true;
          return searchCols.some((c) => c.getSearchValue!(r).toLowerCase().includes(q));
        });
      }
    }

    // 2. Apply toolbar dropdown filters
    if (!onFilterChange && filters) {
      filters.forEach(({ key }) => {
        const val = filterVals[key] ?? '';
        if (val) rows = rows.filter((r) => String((r as any)[key]) === val);
      });
    }

    // 3. Apply column-level filters
    Object.entries(colFilters).forEach(([key, filter]) => {
      if (!filter) return;
      const col = columns.find((c) => c.key === key);
      const fk = col?.filterKey ?? key;
      if (filter.type === 'range') {
        const { min, max } = filter;
        if (min !== '')
          rows = rows.filter((r) => {
            const v = (r as any)[fk];
            const num = typeof v === 'number' ? v : Number(v);
            return !isNaN(num) && num >= Number(min);
          });
        if (max !== '')
          rows = rows.filter((r) => {
            const v = (r as any)[fk];
            const num = typeof v === 'number' ? v : Number(v);
            return !isNaN(num) && num <= Number(max);
          });
      } else if (filter.type === 'dateRange') {
        const { from, to } = filter;
        if (from !== '')
          rows = rows.filter((r) => {
            const v = (r as any)[fk];
            if (!v) return false;
            return new Date(v) >= new Date(from);
          });
        if (to !== '')
          rows = rows.filter((r) => {
            const v = (r as any)[fk];
            if (!v) return false;
            return new Date(v) <= new Date(`${to}T23:59:59`);
          });
      } else if (filter.type === 'values' && filter.selected.length > 0) {
        const selSet = new Set(filter.selected);
        rows = rows.filter((r) => {
          const v = (r as any)[fk];
          return selSet.has(String(v ?? ''));
        });
      }
    });

    // 3. Apply sort LAST
    if (sortKey && sortDir && !onSortChange) {
      const sortCol = columns.find((c) => c.key === sortKey);
      rows.sort((a, b) => {
        const av = sortCol?.getSortValue ? sortCol.getSortValue(a) : (a as any)[sortKey];
        const bv = sortCol?.getSortValue ? sortCol.getSortValue(b) : (b as any)[sortKey];
        let cmp = 0;
        if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv;
        } else {
          const as = String(av ?? '');
          const bs = String(bv ?? '');
          cmp = as < bs ? -1 : as > bs ? 1 : 0;
        }
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return rows;
  }, [
    data,
    searchVal,
    strictColumnSearch,
    serverSideSearch,
    filterVals,
    filters,
    onFilterChange,
    colFilters,
    sortKey,
    sortDir,
    onSortChange,
    columns,
  ]);

  // ─ Pagination ────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);

  const paginatedData = useMemo(() => {
    if (!pagination) return processed;
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return processed.slice(start, end);
  }, [processed, pagination, currentPage, pageSize]);

  const pageCount = useMemo(() => {
    if (!pagination) return 1;
    return Math.ceil(processed.length / pageSize);
  }, [processed.length, pagination, pageSize]);

  // Expose the processed (searched/filtered/sorted) rows to the parent, e.g. for exporting.
  useEffect(() => {
    onProcessedDataChange?.(processed);
  }, [processed, onProcessedDataChange]);

  // Reset to first page when data changes significantly
  useEffect(() => {
    setCurrentPage(0);
  }, [searchVal, filterVals, colFilters, sortKey, sortDir]);

  // ─ Selection state (NOW OPTIONAL) ─────────────────────────────────────────
  const [internalSelectedIndices, setInternalSelectedIndices] = useState<Set<number>>(new Set());

  // Support both controlled (external) and uncontrolled (internal) selection
  // In controlled mode, derive from selectedRowIds; otherwise use internal state
  // IMPORTANT: iterate over paginatedData (sorted/filtered order) so that indices
  // match what the render loop sees — iterating over `data` caused highlights to
  // stay at the original position after sorting.
  const selectedIndices: Set<number> = useMemo(
    () =>
      selectedRowIds !== undefined
        ? new Set(
            paginatedData
              .map((row, idx) => {
                const rowId = rowKey ? rowKey(row, idx) : (row as any).id ?? idx;
                return selectedRowIds.has(rowId) ? idx : -1;
              })
              .filter((idx): idx is number => idx >= 0)
          )
        : internalSelectedIndices,
    [selectedRowIds, paginatedData, rowKey, internalSelectedIndices]
  );

  const isControlled = selectedRowIds !== undefined;

  // Only count rows that are actually selectable (isRowSelectable may exclude some rows)
  const selectableIndices = useMemo(
    () =>
      paginatedData
        .map((row, i) => i)
        .filter((i) => !isRowSelectable || isRowSelectable(paginatedData[i], i)),
    [paginatedData, isRowSelectable]
  );
  const allSelected =
    selectable &&
    selectableIndices.length > 0 &&
    selectableIndices.every((i) => selectedIndices.has(i));
  const someSelected = selectable && selectedIndices.size > 0;

  const toggleSelectAll = useCallback(() => {
    if (!selectable) return;

    if (isControlled) {
      // Controlled mode - call onSelectionChange with all/none
      if (onSelectionChange) {
        if (allSelected) {
          // Deselect all - pass empty array
          onSelectionChange([], []);
        } else {
          // Select only selectable rows
          onSelectionChange(
            selectableIndices.map((i) => paginatedData[i]),
            selectableIndices
          );
        }
      }
    } else {
      // Uncontrolled mode - update internal state
      setInternalSelectedIndices((prev) => {
        const next = new Set(prev);
        if (allSelected) {
          selectableIndices.forEach((i) => next.delete(i));
        } else {
          selectableIndices.forEach((i) => next.add(i));
        }
        if (onSelectionChange) {
          const indices = Array.from(next);
          onSelectionChange(
            indices.map((idx) => paginatedData[idx]),
            indices
          );
        }
        return next;
      });
    }
  }, [allSelected, selectableIndices, paginatedData, onSelectionChange, selectable, isControlled]);

  const toggleSelectRow = useCallback(
    (globalIndex: number) => {
      if (!selectable) return;

      if (isControlled) {
        // Controlled mode - call onSelectionChange with single row toggle
        if (onSelectionChange) {
          const currentlySelected = selectedIndices.has(globalIndex);
          if (currentlySelected) {
            // Deselect this row - need to pass all OTHER selected rows
            const indices = Array.from(selectedIndices).filter((idx) => idx !== globalIndex);
            onSelectionChange(
              indices.map((idx) => paginatedData[idx]),
              indices
            );
          } else {
            // Select this row - add to existing selection
            const indices = Array.from(selectedIndices).concat([globalIndex]);
            onSelectionChange(
              indices.map((idx) => paginatedData[idx]),
              indices
            );
          }
        }
      } else {
        // Uncontrolled mode - update internal state
        setInternalSelectedIndices((prev) => {
          const next = new Set(prev);
          if (next.has(globalIndex)) next.delete(globalIndex);
          else next.add(globalIndex);
          if (onSelectionChange) {
            const indices = Array.from(next);
            onSelectionChange(
              indices.map((idx) => paginatedData[idx]),
              indices
            );
          }
          return next;
        });
      }
    },
    [onSelectionChange, paginatedData, selectable, isControlled, selectedIndices]
  );

  // ─ Virtualization ─────────────────────────────────────────────────────────
  const footerRef = useRef<HTMLDivElement>(null);

  const clearSelection = useCallback(() => {
    if (isControlled) {
      if (onSelectionChange) {
        onSelectionChange([], []);
      }
    } else {
      setInternalSelectedIndices(new Set());
    }
  }, [isControlled, onSelectionChange]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (footerRef.current) footerRef.current.scrollLeft = el.scrollLeft;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const prevLoading = useRef(loading);
  useEffect(() => {
    if (prevLoading.current && !loading && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    prevLoading.current = loading;
  }, [loading]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [searchVal]);

  // ─ Render ─────────────────────────────────────────────────────────────────

  const colCount = visibleColumns.length + (rowActions?.length ? 1 : 0) + (selectable ? 1 : 0);
  const enableScrollX = scrollX && !effectiveResizable;

  // Calculate pagination info
  const paginationFrom = pagination ? currentPage * pageSize + 1 : 1;
  const paginationTo = pagination
    ? Math.min((currentPage + 1) * pageSize, processed.length)
    : processed.length;
  const paginationLabel = countLabel ?? 'items';

  return (
    <>
      <div ref={ref} className={twMerge('flex flex-col gap-3 h-full', className)}>
        {/* ── Summary cards (OPTIONAL) ── */}
        {!isMobile && summaryCards && summaryCards.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {summaryCards.map((card, i) => {
              const variantCls = cardVariantCls[card.variant ?? 'default'];
              return (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs text-gray-500">{card.label}</p>
                    {card.icon && (
                      <div
                        className={twMerge(
                          'w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
                          variantCls
                        )}>
                        {card.icon}
                      </div>
                    )}
                  </div>
                  <p
                    className={twMerge(
                      'text-lg font-semibold',
                      card.variant === 'danger'
                        ? 'text-red-600'
                        : card.variant === 'warning'
                        ? 'text-amber-600'
                        : card.variant === 'success'
                        ? 'text-green-600'
                        : 'text-gray-900'
                    )}>
                    {card.value}
                  </p>
                  {card.subLabel && <p className="text-xs text-gray-400 mt-0.5">{card.subLabel}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Column toggle pills ── */}
        {columnToggle && (
          <div className="flex gap-1.5 flex-wrap">
            {columns
              .filter((c) => c.toggleable)
              .map((c) => (
                <button
                  key={c.key}
                  onClick={() => toggleCol(c.key)}
                  className={twMerge(
                    'px-3 py-1 rounded-full text-xs font-medium border transition-colors select-none',
                    !hiddenCols.has(c.key)
                      ? 'bg-blue-50 text-blue-500 border-blue-200'
                      : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-300'
                  )}>
                  {c.label}
                </button>
              ))}
          </div>
        )}

        {/* ── Toolbar ── */}
        {(search || filters?.length || addButton || toolbarLeft || toolbarRight) && (
          <div className="flex items-start gap-2 flex-wrap">
            {search && (
              <div className="relative">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchVal}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={`h-8 pl-8 pr-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-50 transition-all w-52 ${searchClassName}`}
                />
              </div>
            )}
            {toolbarLeft}

            {filters &&
              filters.map((f) => (
                <select
                  key={f.key}
                  value={filterVals[f.key] ?? ''}
                  onChange={(e) => handleFilter(f.key, e.target.value)}
                  className="h-8 px-2 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 outline-none focus:border-blue-300 transition-colors">
                  <option value="">{f.placeholder ?? `All ${f.label.toLowerCase()}`}</option>
                  {f.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ))}

            {/* Right side: add button + count + custom slot */}
            <div className="ml-auto flex items-center gap-2">
              {toolbarRight}
              {addButton && (
                <button
                  onClick={addButton.onClick}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-white bg-blue-500 hover:bg-blue-700 rounded-lg transition-colors">
                  {addButton.icon}
                  {addButton.label}
                </button>
              )}
              <div>
                {showCount && (
                  <div className="flex items-center gap-1.5">
                    {loadingMore ? (
                      <span className="flex items-center text-[10px] text-amber-500">
                        <svg
                          className="animate-spin shrink-0"
                          width="9"
                          height="9"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      </span>
                    ) : (
                      <div
                        className={twMerge(
                          'w-2 h-2 rounded-full shrink-0',
                          loading ? 'bg-amber-400' : 'bg-green-500'
                        )}
                      />
                    )}
                    <span className="text-xs text-gray-500">
                      <span className="font-medium text-gray-800">{processed.length}</span>
                      {totalCount && data.length < totalCount
                        ? `/${totalCount} (${Math.round((data.length / totalCount) * 100)}%)`
                        : ''}{' '}
                      {countLabel}
                    </span>
                  </div>
                )}
                {countAddon && <div className="mt-0.5">{countAddon}</div>}
              </div>
            </div>
          </div>
        )}

        {/* ── Active filters bar ── */}
        {showActiveFilters && activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs">
            <span className="text-gray-500">
              Showing <span className="font-medium text-gray-700">{processed.length}</span> of{' '}
              <span className="font-medium text-gray-700">{data.length}</span> {countLabel}
            </span>
            <div className="h-4 w-px bg-gray-200" />
            <button
              onClick={handleClearAllFilters}
              className="text-blue-500 hover:text-blue-700 font-medium transition-colors">
              Clear All Filters
            </button>
            <div className="h-4 w-px bg-gray-200" />
            <span className="text-gray-500">Active Filters:</span>
            {activeFilters.map((filter) => (
              <span
                key={`${filter.source}-${filter.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-md text-blue-700">
                <span className="font-medium">{filter.label}:</span>{' '}
                <span className="max-w-[150px] truncate">{filter.value}</span>
                <button
                  onClick={() => handleClearSingleFilter(filter.id, filter.source)}
                  className="ml-0.5 hover:text-blue-900 transition-colors">
                  <X size={12} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* ── Caller-supplied content between the toolbar and the table ── */}
        {belowToolbar}

        {/* ── Bulk action bar (only if selectable) ── */}
        {selectable && someSelected && bulkActions && bulkActions.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl">
            <span className="text-xs font-medium text-blue-700 mr-2">
              {selectedIndices.size} selected
            </span>
            {bulkActions.map((action) => (
              <button
                key={action.key}
                onClick={() => {
                  const indices = Array.from(selectedIndices);
                  action.onClick(
                    indices.map((idx) => paginatedData[idx]),
                    indices
                  );
                }}
                className={twMerge(
                  'h-7 px-3 text-xs font-medium rounded-lg border transition-colors',
                  action.variant === 'danger'
                    ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                    : 'border-blue-200 bg-white text-blue-500 hover:bg-blue-100'
                )}>
                {action.label}
              </button>
            ))}
            <button
              onClick={clearSelection}
              className="ml-auto text-xs text-blue-500 hover:text-blue-700">
              Clear
            </button>
          </div>
        )}

        {/* ── Table wrapper ── */}
        <div
          ref={tableWrapperRef}
          className={twMerge(
            'border border-gray-200 rounded-xl flex flex-col flex-1 min-h-0 bg-white overflow-hidden',
            tableClassName,
            !isMobile && (enableScrollX ? 'overflow-hidden' : 'overflow-x-clip')
          )}>
          {/* ── Card view (mobile) ── */}
          {isMobile && (
            <div
              className="overflow-y-auto flex-1 min-h-0"
              style={maxHeight ? { maxHeight } : undefined}>
              {loading && (
                <div className="flex items-center justify-center gap-2 py-10 text-xs text-gray-400">
                  <svg
                    className="animate-spin"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  {loadingText}
                </div>
              )}
              {!loading && paginatedData.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-3 py-16">
                  <img src={emptyListImg} alt="" className="w-24 object-contain opacity-40" />
                  <p className="text-xs text-gray-400">{emptyMessage}</p>
                </div>
              )}
              {!loading && paginatedData.length > 0 && (
                <div className="flex flex-col gap-2 p-2">
                  {paginatedData.map((row, i) => {
                    const key = rowKey ? rowKey(row, i) : i;
                    const extraCls = rowClassName ? rowClassName(row, i) : '';

                    if (renderMobileCard) {
                      return (
                        <div
                          key={key}
                          className={twMerge(onRowClick && 'cursor-pointer', extraCls)}
                          onClick={onRowClick ? (e) => onRowClick(row, i, e) : undefined}>
                          {renderMobileCard(row, i)}
                        </div>
                      );
                    }

                    const dataCols = effectiveColumns.filter((c) => c.label !== '');
                    const actionCols = effectiveColumns.filter((c) => c.label === '');
                    const [firstCol, ...restCols] = dataCols;
                    return (
                      <div
                        key={key}
                        className={twMerge(
                          'bg-white border border-gray-100 rounded-xl p-4',
                          onRowClick && 'cursor-pointer active:bg-gray-50',
                          extraCls
                        )}
                        onClick={onRowClick ? (e) => onRowClick(row, i, e) : undefined}>
                        {/* Primary field */}
                        {firstCol && (
                          <div className="mb-3 pb-2 border-b border-gray-100">
                            {firstCol.render ? (
                              firstCol.render((row as any)[firstCol.key], row, i)
                            ) : (
                              <span className="text-sm font-medium text-gray-900">
                                {(row as any)[firstCol.key] ?? '—'}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Secondary fields */}
                        {restCols.length > 0 && (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-3">
                            {restCols.map((col) => (
                              <div key={col.key} className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">
                                  {col.label}
                                </p>
                                <div className="text-xs text-gray-700">
                                  {col.render
                                    ? col.render((row as any)[col.key], row, i)
                                    : (row as any)[col.key] ?? '—'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Action columns */}
                        {actionCols.length > 0 && (
                          <div
                            className="flex justify-end gap-2 pt-2 border-t border-gray-100"
                            onClick={(e) => e.stopPropagation()}>
                            {actionCols.map((col) =>
                              col.render ? (
                                <div key={col.key}>{col.render((row as any)[col.key], row, i)}</div>
                              ) : null
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Table view (desktop) ── */}
          {!isMobile && (
            <div
              ref={scrollContainerRef}
              className={twMerge(
                enableScrollX ? 'overflow-auto flex-1 min-h-0' : 'overflow-y-auto flex-1 min-h-0',
                (loading || paginatedData.length === 0) && 'overflow-hidden'
              )}
              style={{
                ...(maxHeight ? { maxHeight } : {}),
                overflowAnchor: 'none',
              }}>
              <table
                className="text-xs border-collapse"
                style={{
                  display: 'grid',
                  ...(effectiveResizable ? { width: '100%' } : { minWidth: '100%' }),
                }}>
                {/* ── Head ── */}
                <thead
                  style={{
                    display: 'grid',
                    position: 'sticky',
                    top: 0,
                    zIndex: 20,
                  }}>
                  <tr
                    className="bg-gray-50 border-b border-gray-200"
                    style={{ display: 'flex', width: '100%' }}>
                    {selectable && (
                      <th
                        className="px-3 py-2.5 w-10 flex items-center sticky left-0 bg-gray-50 z-20"
                        style={{ position: 'sticky' }}>
                        <IndeterminateCheckbox
                          checked={!!allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    {effectiveColumns.map((col) => {
                      const isSortable = col.sortable ?? sortable;
                      const isActive = sortKey === col.key && sortDir !== '';
                      const align = col.headerAlign ?? (col.numeric ? 'right' : 'left');
                      const filterActive = isColFilterActive(colFilters[col.key]);
                      const sticky = stickyOffsets[col.key];

                      return (
                        <th
                          key={col.key}
                          onClick={isSortable && !loading ? () => handleSort(col.key) : undefined}
                          className={twMerge(
                            'px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 select-none flex items-center relative',
                            'flex-shrink-0',
                            sticky?.firstLeft && selectable && 'pl-1',
                            enableScrollX && 'whitespace-nowrap',
                            isSortable && !loading
                              ? 'cursor-pointer group hover:text-gray-500 transition-colors'
                              : '',
                            isActive && 'text-blue-500',
                            align === 'right' && 'justify-end',
                            align === 'center' && 'justify-center',
                            verticalDividers && 'border-r border-gray-200 last:border-r-0',
                            sticky && 'bg-gray-50 border-gray-200 border-b overflow-hidden',
                            sticky?.edgeLeft && 'border-r border-gray-100',
                            sticky?.edgeRight && 'border-l border-gray-100'
                          )}
                          style={{
                            width: col.width,
                            minWidth: effectiveResizable ? 60 : col.width,
                            ...(sticky
                              ? {
                                  position: 'sticky',
                                  left: sticky.left,
                                  right: sticky.right,
                                  zIndex: 4,
                                }
                              : {}),
                          }}>
                          <div className="flex items-center gap-1">
                            <span>{col.label}</span>
                            {isSortable && <SortIcon active={isActive} dir={sortDir} />}
                            {col.filterable && (
                              <button
                                className="inline-flex shrink-0 ml-0.5"
                                disabled={loading}
                                onClick={(e) => handleFilterIconClick(col.key, e)}>
                                <Funnel
                                  size={10}
                                  weight={filterActive ? 'fill' : 'regular'}
                                  className={
                                    filterActive
                                      ? 'text-blue-500'
                                      : 'text-gray-300 group-hover:text-gray-400 transition-colors'
                                  }
                                />
                              </button>
                            )}
                          </div>
                          {resizable && (
                            <div
                              className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors z-10"
                              onMouseDown={(e) => onResizeMouseDown(col.key, e)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </th>
                      );
                    })}
                    {rowActions && rowActions.length > 0 && (
                      <th
                        className={twMerge(
                          'px-4 py-2.5 flex-shrink-0',
                          stickyRowActions && 'bg-gray-50 border-l border-gray-200'
                        )}
                        style={{
                          width: getRowActionsWidth(rowActions.length),
                          // Above the other header cells (z 4), which scroll under it.
                          ...(stickyRowActions ? { position: 'sticky', right: 0, zIndex: 6 } : {}),
                        }}
                      />
                    )}
                  </tr>
                </thead>

                {/* ── Body ── */}
                <DillVirtualBody
                  rows={paginatedData}
                  rowHeight={rowHeight}
                  scrollContainerRef={scrollContainerRef}
                  loading={loading}
                  loadingText={loadingText}
                  colCount={colCount}
                  emptyMessage={emptyMessage}
                  emptyAction={emptyAction}
                  selectable={selectable}
                  selectedIndices={selectedIndices}
                  toggleSelectRow={toggleSelectRow}
                  visibleColumns={effectiveColumns}
                  rowActions={rowActions}
                  rowActionsMode={rowActionsMode}
                  stickyRowActions={stickyRowActions}
                  onRowClick={onRowClick}
                  rowClassName={rowClassName}
                  isRowSelectable={isRowSelectable}
                  getRowDisabledReason={getRowDisabledReason}
                  striped={striped}
                  hoverVariant={hoverVariant}
                  rowKey={rowKey}
                  stickyOffsets={stickyOffsets}
                  rowPadding={rowPadding}
                  resizable={effectiveResizable}
                  verticalDividers={verticalDividers}
                />
              </table>
            </div>
          )}

          {/* ── Footer (outside scroll container so it always sits at the bottom) ── */}
          {!isMobile && showFooter && visibleColumns.some((c) => c.footer !== undefined) && (
            <div
              ref={footerRef}
              className="bg-gray-50 border-t-2 border-gray-200 font-medium flex flex-row shrink-0 text-xs overflow-x-hidden"
              style={{ minWidth: '100%' }}>
              {selectable && <div className="px-3 py-2.5 w-10 flex-shrink-0" />}
              {effectiveColumns.map((col) => {
                const sticky = stickyOffsets[col.key];
                return (
                  <div
                    key={col.key}
                    className={twMerge(
                      'px-4 py-2.5 text-xs text-gray-700 flex-shrink-0',
                      col.numeric && 'text-right tabular-nums',
                      verticalDividers && 'border-r border-gray-200 last:border-r-0',
                      sticky && 'bg-gray-50',
                      sticky?.edgeLeft && 'border-r border-gray-100',
                      sticky?.edgeRight && 'border-l border-gray-100'
                    )}
                    style={{
                      width: col.width,
                      minWidth: col.width,
                      ...(sticky
                        ? {
                            position: 'sticky',
                            left: sticky.left,
                            right: sticky.right,
                            zIndex: 1,
                          }
                        : {}),
                    }}>
                    {col.footer ?? ''}
                  </div>
                );
              })}
              {rowActions?.length ? (
                <div
                  className={twMerge(
                    'px-4 py-2.5 flex-shrink-0',
                    stickyRowActions && 'bg-gray-50 border-l border-gray-200'
                  )}
                  style={{
                    width: getRowActionsWidth(rowActions.length),
                    ...(stickyRowActions ? { position: 'sticky', right: 0 } : {}),
                  }}
                />
              ) : null}
            </div>
          )}

          {/* ── Pagination ── */}
          {!isMobile && pagination && (
            <Pagination
              page={currentPage}
              pageCount={pageCount}
              from={paginationFrom}
              to={paginationTo}
              total={processed.length}
              label={paginationLabel}
              onPage={setCurrentPage}
            />
          )}
        </div>
      </div>
      {activeFilterCol &&
        createPortal(
          <div
            ref={filterPanelRef}
            style={{
              position: 'fixed',
              top: filterAnchor.top,
              left: filterAnchor.left,
              zIndex: 9999,
              width: 230,
            }}
            className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {visibleColumns.find((c) => c.key === activeFilterCol)?.label ?? activeFilterCol}
              </span>
              <button
                onClick={() => clearColFilter(activeFilterCol)}
                className="text-xs text-red-400 hover:text-red-600 flex items-center gap-0.5">
                <X size={10} /> Clear
              </button>
            </div>
            {visibleColumns.find((c) => c.key === activeFilterCol)?.filterType === 'dateRange' ? (
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] text-gray-400 font-medium">From</label>
                <input
                  type="date"
                  value={
                    (colFilters[activeFilterCol] as ColDateRangeFilter | undefined)?.from ?? ''
                  }
                  onChange={(e) => handleDateRangeChange(activeFilterCol, 'from', e.target.value)}
                  className="h-7 px-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-300"
                />
                <label className="text-[10px] text-gray-400 font-medium">To</label>
                <input
                  type="date"
                  value={(colFilters[activeFilterCol] as ColDateRangeFilter | undefined)?.to ?? ''}
                  onChange={(e) => handleDateRangeChange(activeFilterCol, 'to', e.target.value)}
                  className="h-7 px-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-300"
                />
              </div>
            ) : visibleColumns.find((c) => c.key === activeFilterCol)?.numeric ? (
              <div className="flex flex-col gap-1.5">
                <input
                  type="number"
                  placeholder="Min"
                  value={(colFilters[activeFilterCol] as ColRangeFilter | undefined)?.min ?? ''}
                  onChange={(e) => handleRangeChange(activeFilterCol, 'min', e.target.value)}
                  className="h-7 px-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-300"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={(colFilters[activeFilterCol] as ColRangeFilter | undefined)?.max ?? ''}
                  onChange={(e) => handleRangeChange(activeFilterCol, 'max', e.target.value)}
                  className="h-7 px-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-300"
                />
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Search values…"
                  value={valFilterSearch[activeFilterCol] ?? ''}
                  onChange={(e) =>
                    setValFilterSearch((p) => ({ ...p, [activeFilterCol]: e.target.value }))
                  }
                  className="h-7 px-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-300"
                />
                <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                  {(() => {
                    const activeCol = visibleColumns.find((c) => c.key === activeFilterCol);
                    const labelFn = activeCol?.getFilterLabel;
                    return (colUniqueValues[activeFilterCol] ?? [])
                      .filter((v) => {
                        const display = labelFn ? labelFn(v) : v;
                        return display
                          .toLowerCase()
                          .includes((valFilterSearch[activeFilterCol] ?? '').toLowerCase());
                      })
                      .map((val) => {
                        const selected = (
                          colFilters[activeFilterCol] as ColValuesFilter | undefined
                        )?.selected?.includes(val);
                        const display = labelFn ? labelFn(val) : val;
                        return (
                          <label
                            key={val}
                            className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                            <input
                              type="checkbox"
                              checked={!!selected}
                              onChange={() => handleToggleValue(activeFilterCol, val)}
                              className="rounded border-gray-300 text-blue-500"
                            />
                            {display}
                          </label>
                        );
                      });
                  })()}
                </div>
              </>
            )}
            <button
              onClick={() => setActiveFilterCol(null)}
              className="mt-1 h-7 w-full rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium transition-colors">
              Apply
            </button>
          </div>,
          document.body
        )}
      <ReactTooltip
        id="dill-table-disabled-row"
        place="right"
        variant="dark"
        opacity="100%"
        style={{ background: '#222', maxWidth: '220px', zIndex: 99999, fontSize: '12px' }}
      />
    </>
  );
}

type DillTableComponent = {
  <T = any>(
    props: DillTableProps<T> & React.RefAttributes<HTMLDivElement>
  ): React.ReactElement | null;
  displayName?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DillTable: DillTableComponent = React.forwardRef(DillTableImpl) as any;
DillTable.displayName = 'DillTable';

export default DillTable;
